import { ChangeEvent, useEffect, useRef, useState } from "react";
import { ethers } from "ethers";
import JicFloatingWidget from "./jicFloatingWidget";
import LoveTransferPanel from "./loveTransferPanel";
import { describeFace, loadFaceRecognitionModels } from "../lib/faceRecognition";
import { resolveFaceOnServer } from "../lib/faceRegistry";

const JIC_URL = "https://u.jic.io";
const LOVE_ADDRESS = "0x16A7F8CfAD687A87183fCbd1dF7aF09dce05D357";

type Anchor = {
  slug: string;
  label: string;
  kind: "relay" | "prereg";
  poolAddress: string | null;
  note: string | null;
  templates: number;
};

// 经典基督画像三联(Wikimedia 公有领域),即锚点的三个已入库模板
const JESUS_TRIPTYCH = [
  { src: "https://upload.wikimedia.org/wikipedia/commons/d/d8/Christ_Icon_Sinai_6th_century_-_crop.jpg", title: "西奈基督圣像 · 6 世纪" },
  { src: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/5c/Leonardo_da_Vinci%2C_Salvator_Mundi%2C_c.1500%2C_oil_on_walnut%2C_45.4_%C3%97_65.6_cm.jpg/960px-Leonardo_da_Vinci%2C_Salvator_Mundi%2C_c.1500%2C_oil_on_walnut%2C_45.4_%C3%97_65.6_cm.jpg", title: "达芬奇《救世主》· c.1500" },
  { src: "https://upload.wikimedia.org/wikipedia/commons/thumb/d/d1/Rembrandt_Harmensz._van_Rijn%2C_Dutch_%28active_Leiden_and_Amsterdam%29_-_Head_of_Christ_-_Google_Art_Project.jpg/960px-Rembrandt_Harmensz._van_Rijn%2C_Dutch_%28active_Leiden_and_Amsterdam%29_-_Head_of_Christ_-_Google_Art_Project.jpg", title: "伦勃朗《基督头像》· c.1648" },
];

function shortAddress(value: string) {
  return `${value.slice(0, 7)}…${value.slice(-5)}`;
}

export default function AnchorShrine({ slug }: { slug: string }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<number | null>(null);
  const analyzingRef = useRef(false);
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  const [anchorChecked, setAnchorChecked] = useState(false);
  const [poolBalance, setPoolBalance] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [recognized, setRecognized] = useState<number | null>(null); // confidence
  const [message, setMessage] = useState("扫描或上传任何一幅经典基督画像");

  useEffect(() => {
    loadFaceRecognitionModels().catch(() => undefined);
    fetch(`${JIC_URL}/v1/face/anchors`)
      .then((response) => response.json())
      .then((data: { anchors?: Anchor[] }) => {
        setAnchor(data.anchors?.find((item) => item.slug === slug) || null);
      })
      .catch(() => setAnchor(null))
      .finally(() => setAnchorChecked(true));
    return stopScanner;
  }, [slug]);

  // 公共池链上余额(只读 RPC)
  useEffect(() => {
    if (!anchor?.poolAddress) return;
    const provider = new ethers.JsonRpcProvider("https://mainnet.base.org", 8453, { staticNetwork: true });
    const contract = new ethers.Contract(LOVE_ADDRESS, ["function balanceOf(address) view returns (uint256)"], provider);
    contract
      .balanceOf(anchor.poolAddress)
      .then((value: bigint) => setPoolBalance(Number(ethers.formatUnits(value, 18)).toFixed(2)))
      .catch(() => setPoolBalance(null));
  }, [anchor?.poolAddress]);

  const stopScanner = () => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setScanning(false);
  };

  const resolveInput = async (input: HTMLVideoElement | HTMLImageElement) => {
    if (analyzingRef.current) return;
    analyzingRef.current = true;
    try {
      const descriptor = await describeFace(input);
      if (!descriptor) {
        setMessage("没有找到清晰面容，请对准画像再试");
        return;
      }
      const server = await resolveFaceOnServer(descriptor);
      if (server?.match && server.match.slug === slug) {
        setRecognized(server.match.confidence);
        setMessage(`识别成功 · 置信度 ${server.match.confidence}%`);
        stopScanner();
      } else if (server?.match) {
        setMessage(`识别到的是 ${server.match.label || "@" + server.match.name}，不是这个锚点`);
      } else {
        setMessage("检测到面容，但与本锚点不匹配");
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "识别失败");
    } finally {
      analyzingRef.current = false;
    }
  };

  const startCamera = async () => {
    stopScanner();
    setRecognized(null);
    setMessage("正在打开相机…");
    try {
      await loadFaceRecognitionModels();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 960 } },
        audio: false,
      });
      streamRef.current = stream;
      if (!videoRef.current) throw new Error("相机初始化失败");
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      setScanning(true);
      setMessage("对准画像、雕像或屏幕上的基督面容…");
      timerRef.current = window.setInterval(() => {
        if (videoRef.current && videoRef.current.readyState >= 2) resolveInput(videoRef.current);
      }, 900);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "无法打开相机");
      stopScanner();
    }
  };

  const onPhoto = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    stopScanner();
    setRecognized(null);
    setMessage("正在识别画像…");
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);
    image.onload = async () => {
      try {
        await resolveInput(image);
      } finally {
        URL.revokeObjectURL(objectUrl);
        event.target.value = "";
      }
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      setMessage("照片读取失败，请换一张");
    };
    image.src = objectUrl;
  };

  const label = anchor?.label || "LOVE 中转站";

  return (
    <main className="face3-page face3-scan-page">
      <div className="face3-aurora face3-aurora-one" />
      <div className="face3-aurora face3-aurora-two" />
      <header className="face3-header">
        <a className="face3-brand" href="https://face.jic.io" aria-label="FACE3 home"><span className="face3-mark">✟</span><span>{slug.toUpperCase()} · JIC</span></a>
        <span className="face3-mvp-pill">RELAY · LOVE</span>
      </header>

      <section className="face3-scan-hero">
        <div className="face3-scan-copy">
          <p className="face3-eyebrow">LOVE RELAY STATION</p>
          <h1>{label.split("·")[0].trim()}<br /><em>LOVE 中转站</em></h1>
          <p>没有人能「是」这个锚点 —— 它属于所有人。扫描任何一幅经典基督画像即可致敬：投入的 LOVE 进入公共池，按透明规则流出成为祝福。</p>
          <div className="face3-scan-actions">
            <button type="button" onClick={startCamera}>⌁ 扫描画像致敬</button>
            <button type="button" className="secondary" onClick={() => fileRef.current?.click()}>从照片识别</button>
            <input ref={fileRef} type="file" accept="image/*" onChange={onPhoto} hidden />
          </div>
          <div className="face3-local-registry">
            <i />
            {anchorChecked && !anchor && "锚点不存在"}
            {anchor && (
              <>公共池：{anchor.poolAddress ? `${shortAddress(anchor.poolAddress)}${poolBalance !== null ? ` · ${poolBalance} LOVE` : ""}` : "地址待设定"} · 模板 {anchor.templates} 幅</>
            )}
          </div>
        </div>

        <div className={`face3-scanner ${scanning ? "is-scanning" : ""}`}>
          <video ref={videoRef} playsInline muted />
          <div className="face3-scan-grid" />
          <div className="face3-scan-corners"><i /><i /><i /><i /></div>
          {!scanning && !recognized && (
            <div className="face3-scan-placeholder">
              <span>✟</span>
              <strong>{slug.toUpperCase()} RELAY</strong>
              <small>扫任何经典画像 · 不需要活体</small>
            </div>
          )}
          {scanning && <div className="face3-scan-line" />}
          <div className={`face3-scan-result state-${recognized ? "matched" : scanning ? "scanning" : "idle"}`}>
            <i />
            <div><strong>{recognized ? `✟ ${label}` : scanning ? "SCANNING" : "READY"}</strong><small>{message}</small></div>
          </div>
        </div>
      </section>

      {recognized !== null && anchor && (
        <LoveTransferPanel
          faceId={label}
          handle={anchor.slug}
          recipient={anchor.poolAddress || undefined}
          verified={Boolean(anchor.poolAddress)}
        />
      )}

      <section className="face3-scan-explainer">
        {JESUS_TRIPTYCH.map((art) => (
          <div key={art.src} style={{ display: "block", padding: 0, overflow: "hidden" }}>
            <img src={art.src} alt={art.title} style={{ display: "block", width: "100%", aspectRatio: "1/1.1", objectFit: "cover" }} loading="lazy" />
            <small style={{ display: "block", padding: "10px 14px", color: "#718895" }}>{art.title}</small>
          </div>
        ))}
      </section>

      <section className="face3-flow">
        <div><span>01</span><strong>扫画像</strong><small>任何时代的基督面容</small></div>
        <i>→</i>
        <div><span>02</span><strong>投入公共池</strong><small>致敬 · 钱包逐笔签名</small></div>
        <i>→</i>
        <div><span>03</span><strong>透明流出</strong><small>按策展规则成为祝福</small></div>
        <i>→</i>
        <div><span>♡</span><strong>正和循环</strong><small>创世 · 路由 · 激励皆供能 NAV</small></div>
      </section>

      <footer className="face3-footer">
        <span>{slug}.jic.io · A LOVE relay, owned by no one, open to everyone.</span>
        <span>公有领域画像 · Wikimedia Commons</span>
      </footer>
      <JicFloatingWidget />
    </main>
  );
}
